/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { PayloadAction, Draft } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { produce } from 'immer';
import {
  defaultAnimationDelayAtStart,
  defaultTimeSpan,
  secondsPerPxFromCanvasWidth,
} from '@opengeoweb/time-slider';
import { LayerType } from '@opengeoweb/webmap';
import type {
  MapPinLocationPayload,
  SetBboxPayload,
  SetStepBackWardOrForward,
  UpdateAllMapDimensionsPayload,
  UpdateLayerInfoPayload,
} from '@opengeoweb/webmap-react';
import type {
  WebMapState,
  SetAutoLayerIdPayload,
  SetMapAnimationStartPayload,
  SetMapAnimationStopPayload,
  SetTimeSliderSpanPayload,
  SetTimeStepPayload,
  SetAnimationStartTimePayload,
  SetAnimationEndTimePayload,
  SetAnimationDelayPayload,
  MoveLayerPayload,
  SetTimeSliderWidthPayload,
  SetTimeSliderCenterTimePayload,
  SetEndTimeOverriding,
  ToggleAutoUpdatePayload,
  ToggleTimestepAutoPayload,
  ToggleTimeSpanAutoPayload,
  ToggleTimeSliderHoverPayload,
  DisableMapPinPayload,
  ToggleTimeSliderIsVisiblePayload,
  SetDockedLayerManagerSize,
  SetDockedLayerManagerPosition,
  SetDockedLayerManagerDimensions,
  ToggleAnimationLengthAutoPayload,
  SetDefaultMapSettingsPayload,
  ToggleClockPayload,
  SetClockPositionSizePayload,
} from './types';
import type {
  Dimension,
  Layer,
  ToggleMapPinIsVisiblePayload,
  ToggleZoomControlsPayload,
  ReduxLayer,
} from '../types';
import { mapChangeDimension, setMapPreset } from './actions';
import type { MapActionOrigin } from './enums';
import {
  produceDraftStateSetWebMapDimension,
  produceDraftStateSetMapDimensionFromLayerChangeDimension,
  createMap,
  moveArrayElements,
  produceTimeSliderTimeSpan,
} from './utils';
import {
  setBboxSync,
  setLayerActionSync,
  setTimeSync,
} from '../../generic/synchronizationActions/actions';
import type { SyncLayerPayloads } from '../../generic/types';
import { layerActions } from '../layer/reducer';

/**
 * Checks if the layer id is already taken in one of the maps.
 * @param state The WebMapState
 * @param layerIdToCheck The layerId to check
 * @returns true if the layerId is already taken somewhere else.
 */
export const checkIfMapLayerIdIsAlreadyTaken = (
  state: WebMapState,
  layerId: string,
): boolean => {
  return state.allIds.some((mapId: string) => {
    const map = state.byId[mapId];
    const doesLayeridExist = (layers: string[]): boolean =>
      layers?.some((id: string): boolean => id === layerId);
    return (
      doesLayeridExist(map.mapLayers) ||
      doesLayeridExist(map.overLayers) ||
      doesLayeridExist(map.baseLayers) ||
      doesLayeridExist(map.featureLayers)
    );
  });
};

/**
 * Addes id's to the layer object. All layers need to have an id for referring.
 * @param layers
 */
const createLayersWithIds = (state: WebMapState, layers: Layer[]): Layer[] => {
  if (!layers) {
    return layers;
  }
  return produce(layers, (draft) => {
    for (const layer of draft) {
      if (!layer.id) {
        console.warn(`Warning: Layer id was not provided`);
      }
    }
  });
};

const layerHasTimeDimension = (layer: Layer | ReduxLayer): boolean =>
  !!layer.dimensions?.some((dimension) => dimension.name === 'time');

export const initialState: WebMapState = {
  byId: {},
  allIds: [],
};

export const slice = createSlice({
  initialState,
  name: 'mapReducer',
  reducers: {
    registerMap: (
      draft: Draft<WebMapState>,
      action: PayloadAction<{ mapId: string }>,
    ) => {
      const { mapId } = action.payload;
      if (!draft.allIds.includes(mapId)) {
        draft.byId[mapId] = createMap({ id: mapId });
        draft.allIds.push(mapId);
      }
    },
    unregisterMap: (
      draft: Draft<WebMapState>,
      action: PayloadAction<{ mapId: string }>,
    ) => {
      const { mapId } = action.payload;
      const mapIndex = draft.allIds.indexOf(mapId);
      if (mapIndex !== -1) {
        delete draft.byId[mapId];
        draft.allIds.splice(mapIndex, 1);
      }
    },
    setStepBackwardOrForward: (
      _draft: Draft<WebMapState>,

      _action: PayloadAction<SetStepBackWardOrForward>,
    ) => {},
    setBbox: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetBboxPayload>,
    ) => {
      const { mapId, bbox } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      const srs = action.payload.srs || null;
      draft.byId[mapId].bbox = bbox;
      if (srs) {
        draft.byId[mapId].srs = srs;
      }
    },
    mapUpdateAllMapDimensions: (
      draft: Draft<WebMapState>,
      action: PayloadAction<UpdateAllMapDimensionsPayload>,
    ) => {
      const { mapId, dimensions } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].dimensions = dimensions.map((dimension) => ({
        ...dimension,
      }));
    },
    mapStartAnimation: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetMapAnimationStartPayload>,
    ) => {
      const { mapId, timeList, start, end } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].isAnimating = true;

      // Animation is defined by one of the following:
      // - start, end and interval for a continuous animation loop with constant interval
      // - timeList, for a custom timeList
      if (start && end) {
        draft.byId[mapId].animationStartTime = start;
        draft.byId[mapId].animationEndTime = end;
        draft.byId[mapId].timeList = undefined;
      }
      if (timeList && timeList.length >= 0) {
        draft.byId[mapId].animationStartTime = undefined;
        draft.byId[mapId].animationEndTime = undefined;
        draft.byId[mapId].timeList = timeList;
      }

      if (
        action.payload.interval &&
        action.payload.interval !== draft.byId[mapId].timeStep
      ) {
        draft.byId[mapId].timeStep = action.payload.interval;
        draft.byId[mapId].isTimestepAuto = false;
      }
    },
    mapStopAnimation: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetMapAnimationStopPayload>,
    ) => {
      const { mapId } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].isAnimating = false;
    },
    setTimeSliderSpan: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetTimeSliderSpanPayload>,
    ) => {
      const { mapId, timeSliderSpan } = action.payload;
      produceTimeSliderTimeSpan(draft, mapId, timeSliderSpan);
    },
    setTimeStep: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetTimeStepPayload>,
    ) => {
      const { mapId, timeStep } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].timeStep = timeStep;
    },
    setAnimationStartTime: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetAnimationStartTimePayload>,
    ) => {
      const { mapId, animationStartTime } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].animationStartTime = animationStartTime;
      draft.byId[mapId].isAnimationLengthAuto = false;
    },
    setAnimationEndTime: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetAnimationEndTimePayload>,
    ) => {
      const { mapId, animationEndTime } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].animationEndTime = animationEndTime;
      draft.byId[mapId].isAnimationLengthAuto = false;
    },
    setAnimationDelay: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetAnimationDelayPayload>,
    ) => {
      const { mapId, animationDelay } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].animationDelay = animationDelay;
    },
    layerMoveLayer: (
      draft: Draft<WebMapState>,
      action: PayloadAction<MoveLayerPayload>,
    ) => {
      const { oldIndex, newIndex, mapId, layerType } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }

      if (layerType !== LayerType.featureLayer) {
        draft.byId[mapId].mapLayers = moveArrayElements(
          draft.byId[mapId].mapLayers,
          oldIndex,
          newIndex,
        );
      } else {
        draft.byId[mapId].featureLayers = moveArrayElements(
          draft.byId[mapId].featureLayers,
          oldIndex,
          newIndex,
        );
      }
    },
    setAutoLayerId: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetAutoLayerIdPayload>,
    ) => {
      const { mapId, layerId, autoTimeStepLayerId, autoUpdateLayerId } =
        action.payload;
      const map = draft.byId[mapId];
      if (!map) {
        return;
      }
      if (autoTimeStepLayerId || autoUpdateLayerId) {
        map.autoTimeStepLayerId = autoTimeStepLayerId;
        map.autoUpdateLayerId = autoUpdateLayerId;
      } else if (layerId) {
        map.autoTimeStepLayerId = layerId;
        map.autoUpdateLayerId = layerId;
      } else {
        map.autoTimeStepLayerId = undefined;
        map.autoUpdateLayerId = undefined;
      }
    },
    setAutoTimestepLayerId: (
      draft: Draft<WebMapState>,
      action: PayloadAction<{
        mapId: string;
        autoTimestepLayerId: string | undefined;
        origin?: MapActionOrigin;
      }>,
    ) => {
      const { mapId, autoTimestepLayerId } = action.payload;
      const map = draft.byId[mapId];
      if (!map) {
        return;
      }
      map.autoTimeStepLayerId = autoTimestepLayerId;
    },
    setAutoUpdateLayerId: (
      draft: Draft<WebMapState>,
      action: PayloadAction<{
        mapId: string;
        autoUpdateLayerId: string | undefined;
        origin?: MapActionOrigin;
      }>,
    ) => {
      const { mapId, autoUpdateLayerId } = action.payload;
      const map = draft.byId[mapId];
      if (!map) {
        return;
      }
      map.autoUpdateLayerId = autoUpdateLayerId;
    },
    setTimeSliderWidth: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetTimeSliderWidthPayload>,
    ) => {
      const { mapId, timeSliderWidth } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].timeSliderWidth = timeSliderWidth;
      draft.byId[mapId].timeSliderSecondsPerPx = secondsPerPxFromCanvasWidth(
        timeSliderWidth,
        draft.byId[mapId].timeSliderSpan || defaultTimeSpan,
      );
    },
    setTimeSliderCenterTime: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetTimeSliderCenterTimePayload>,
    ) => {
      const { mapId, timeSliderCenterTime } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].timeSliderCenterTime = timeSliderCenterTime;
    },
    toggleAutoUpdate: (
      draft: Draft<WebMapState>,
      action: PayloadAction<ToggleAutoUpdatePayload>,
    ) => {
      const { mapId, shouldAutoUpdate } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].isAutoUpdating = shouldAutoUpdate;
    },
    setEndTimeOverriding: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetEndTimeOverriding>,
    ) => {
      const { mapId, shouldEndtimeOverride } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].shouldEndtimeOverride = shouldEndtimeOverride;
    },
    toggleTimestepAuto: (
      draft: Draft<WebMapState>,
      action: PayloadAction<ToggleTimestepAutoPayload>,
    ) => {
      const { mapId, timestepAuto } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].isTimestepAuto = timestepAuto;
    },
    toggleTimeSpanAuto: (
      draft: Draft<WebMapState>,
      action: PayloadAction<ToggleTimeSpanAutoPayload>,
    ) => {
      const { mapId, timeSpanAuto } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].isTimeSpanAuto = timeSpanAuto;
    },
    toggleAnimationLengthAuto: (
      draft: Draft<WebMapState>,
      action: PayloadAction<ToggleAnimationLengthAutoPayload>,
    ) => {
      const { mapId, autoAnimationLength } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].isAnimationLengthAuto = autoAnimationLength;
    },
    toggleTimeSliderHover: (
      draft: Draft<WebMapState>,
      action: PayloadAction<ToggleTimeSliderHoverPayload>,
    ) => {
      const { mapId, isTimeSliderHoverOn } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].isTimeSliderHoverOn = isTimeSliderHoverOn;
    },
    toggleTimeSliderIsVisible: (
      draft: Draft<WebMapState>,
      action: PayloadAction<ToggleTimeSliderIsVisiblePayload>,
    ) => {
      const { mapId, isTimeSliderVisible } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].isTimeSliderVisible = isTimeSliderVisible;
    },
    toggleZoomControls: (
      draft: Draft<WebMapState>,
      action: PayloadAction<ToggleZoomControlsPayload>,
    ) => {
      const { mapId, shouldShowZoomControls } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].shouldShowZoomControls = shouldShowZoomControls;
    },
    setMapPinLocation: (
      draft: Draft<WebMapState>,
      action: PayloadAction<MapPinLocationPayload>,
    ) => {
      const { mapId, mapPinLocation } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].mapPinLocation = mapPinLocation;
    },
    setDisableMapPin: (
      draft: Draft<WebMapState>,
      action: PayloadAction<DisableMapPinPayload>,
    ) => {
      const { mapId, disableMapPin } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].disableMapPin = disableMapPin;
    },
    toggleMapPinIsVisible: (
      draft: Draft<WebMapState>,
      action: PayloadAction<ToggleMapPinIsVisiblePayload>,
    ) => {
      const { mapId, displayMapPin } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].displayMapPin = displayMapPin;
    },
    setDockedLayerManagerSize: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetDockedLayerManagerSize>,
    ) => {
      const { mapId, dockedLayerManagerSize } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].dockedLayerManagerSize = dockedLayerManagerSize;
    },
    setDockedLayerManagerPosition: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetDockedLayerManagerPosition>,
    ) => {
      const { mapId, dockedLayerManagerPosition } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].dockedLayerManagerPosition = dockedLayerManagerPosition;
    },
    setDockedLayerManagerDimensions: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetDockedLayerManagerDimensions>,
    ) => {
      const { mapId, dockedLayerManagerDimensions } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].dockedLayerManagerDimensions =
        dockedLayerManagerDimensions;
    },
    setMapPresetError: (
      _draft: Draft<WebMapState>,
      _action: PayloadAction<{ mapId: string; error?: string }>,
    ) => {},
    setDefaultMapSettings: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetDefaultMapSettingsPayload>,
    ) => {
      draft.defaultMapSettings = action.payload.preset;
    },
    changeOverlayer: (
      draft: Draft<WebMapState>,
      action: PayloadAction<{
        mapId: string;
        selectedOverlayer: string;
        origin: string;
      }>,
    ) => {
      const { mapId, selectedOverlayer } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].overLayers = [selectedOverlayer];
    },
    toggleClock: (
      draft: Draft<WebMapState>,
      action: PayloadAction<ToggleClockPayload>,
    ) => {
      const { mapId, isClockVisible } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].isClockVisible = isClockVisible;
      if (!isClockVisible) {
        // close options menu when clock is closed
        draft.byId[mapId].isClockOptionsMenuVisible = false;
      }
    },
    toggleClockOptions: (
      draft: Draft<WebMapState>,
      action: PayloadAction<{
        mapId: string;
        isClockOptionsMenuVisible: boolean;
        origin?: string;
      }>,
    ) => {
      const { mapId, isClockOptionsMenuVisible } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      draft.byId[mapId].isClockOptionsMenuVisible = isClockOptionsMenuVisible;
    },
    setClockPositionSize: (
      draft: Draft<WebMapState>,
      action: PayloadAction<SetClockPositionSizePayload>,
    ) => {
      const { mapId, position, size } = action.payload;
      if (!draft.byId[mapId]) {
        return;
      }
      if (position) {
        draft.byId[mapId].clockPosition = position;
      }
      if (size) {
        draft.byId[mapId].clockSize = size;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(layerActions.addLayer, (draft, action) => {
        const { layerId, mapId, layer } = action.payload;
        if (!draft.byId[mapId]) {
          return;
        }

        if (checkIfMapLayerIdIsAlreadyTaken(draft, layerId)) {
          console.warn(`Warning: Layer id ${layerId} was already taken`);
          return;
        }
        const isFeatureLayer = layer?.layerType === LayerType.featureLayer;

        if (
          !isFeatureLayer &&
          !draft.byId[mapId].autoUpdateLayerId &&
          !draft.byId[mapId].autoTimeStepLayerId &&
          layerHasTimeDimension(layer)
        ) {
          draft.byId[mapId].autoUpdateLayerId = layerId;
          draft.byId[mapId].autoTimeStepLayerId = layerId;
        }

        if (isFeatureLayer) {
          draft.byId[mapId].featureLayers.unshift(layerId);
        } else {
          draft.byId[mapId].mapLayers.unshift(layerId);
        }
      })
      .addCase(layerActions.duplicateMapLayer, (draft, action) => {
        const { newLayerId, oldLayerId, mapId } = action.payload;
        if (!draft.byId[mapId]) {
          return;
        }
        if (checkIfMapLayerIdIsAlreadyTaken(draft, newLayerId)) {
          console.warn(`Warning: Layer id ${newLayerId} was already taken`);
          return;
        }
        const isFeatureLayer = draft.byId[mapId].featureLayers.some(
          (id) => id === oldLayerId,
        );
        if (!isFeatureLayer) {
          draft.byId[mapId].mapLayers.unshift(newLayerId);
        } else {
          draft.byId[mapId].featureLayers.unshift(newLayerId);
        }
      })
      .addCase(layerActions.addBaseLayer, (draft, action) => {
        const { layer, layerId, mapId } = action.payload;
        if (!draft.byId[mapId]) {
          return;
        }
        if (checkIfMapLayerIdIsAlreadyTaken(draft, layerId)) {
          console.warn(`Warning: Layer id ${layerId} was already taken`);
          return;
        }
        if (layer.layerType === LayerType.baseLayer) {
          draft.byId[mapId].baseLayers.unshift(layerId);
          return;
        }
        if (layer.layerType === LayerType.overLayer) {
          draft.byId[mapId].overLayers.unshift(layerId);
        }
      })
      .addCase(layerActions.setLayers, (draft, action) => {
        const { mapId } = action.payload;
        const layersWithIds = createLayersWithIds(draft, action.payload.layers);
        const layerIds = layersWithIds.map(({ id }) => id!);
        const activeLayerId = layersWithIds.find(layerHasTimeDimension)?.id;

        if (draft.byId[mapId]) {
          draft.byId[mapId].mapLayers = layerIds;
          draft.byId[mapId].autoUpdateLayerId = activeLayerId;
          draft.byId[mapId].autoTimeStepLayerId = activeLayerId;
        }
      })
      .addCase(layerActions.setBaseLayers, (draft, action) => {
        const { layers, mapId } = action.payload;
        // Split into base and overlayers
        const baseLayers: ReduxLayer[] = [];
        const overLayers: ReduxLayer[] = [];
        layers.forEach((layer) => {
          if (layer.layerType === LayerType.overLayer) {
            overLayers.push(layer);
          } else if (layer.layerType === LayerType.baseLayer) {
            baseLayers.push(layer);
          }
        });

        const baseLayersWithIds = createLayersWithIds(draft, baseLayers);
        const overLayersWithIds = createLayersWithIds(draft, overLayers);

        const baseLayerIds = baseLayersWithIds.map(({ id }) => id!);
        const overLayerIds = overLayersWithIds.map(({ id }) => id!);

        if (draft.byId[mapId]) {
          if (baseLayerIds.length !== 0) {
            draft.byId[mapId].baseLayers = baseLayerIds;
          }
          if (overLayerIds.length !== 0) {
            draft.byId[mapId].overLayers = overLayerIds;
          }
        }
      })
      .addCase(layerActions.layerDelete, (draft, action) => {
        const { mapId, layerId } = action.payload;
        const map = draft.byId[mapId];
        if (!map) {
          return;
        }
        map.mapLayers = map.mapLayers.filter((id) => id !== layerId);

        // if auto layer is deleted, change auto layer ids
        if (!map.mapLayers.length) {
          map.autoUpdateLayerId = undefined;
          map.autoTimeStepLayerId = undefined;
          return;
        }
        const [activeLayerId] = map.mapLayers;
        if (map.autoUpdateLayerId === layerId) {
          map.autoUpdateLayerId = activeLayerId;
        }
        if (map.autoTimeStepLayerId === layerId) {
          map.autoTimeStepLayerId = activeLayerId;
        }
        map.featureLayers = map.featureLayers.filter((id) => id !== layerId);
      })
      .addCase(layerActions.baseLayerDelete, (draft, action) => {
        const { mapId, layerId } = action.payload;
        if (!draft.byId[mapId]) {
          return;
        }
        draft.byId[mapId].baseLayers = draft.byId[mapId].baseLayers.filter(
          (id) => id !== layerId,
        );
        draft.byId[mapId].overLayers = draft.byId[mapId].overLayers.filter(
          (id) => id !== layerId,
        );
      })
      .addCase(layerActions.layerChangeDimension, (draft, action) => {
        const { layerId, dimension } = action.payload;
        produceDraftStateSetMapDimensionFromLayerChangeDimension(
          draft,
          layerId,
          dimension,
        );
      })
      .addCase(setTimeSync, (draft, action) => {
        const { targets: targetsFromAction, source } = action.payload;
        /* Because we want backwards compatibility with the previous code, we also need to listen to the original source action */
        const targets = [];
        if (source) {
          targets.push({
            targetId: source.payload.sourceId,
            value: source.payload.value,
          });
        }
        /* And then append the targets form the action */
        targets.push(...targetsFromAction);
        targets.forEach((target) => {
          const { targetId, value } = target;

          if (draft.byId[targetId]) {
            // SetTimeSliderSpan
            if (value?.timeSliderSpan!) {
              produceTimeSliderTimeSpan(draft, targetId, value.timeSliderSpan);
            }

            // SetTimeSliderCenterTime
            if (value?.timeSliderCenterTime!) {
              draft.byId[targetId].timeSliderCenterTime =
                value.timeSliderCenterTime;
            }

            // SetTimeSliderStep
            if (value?.timeSliderStep!) {
              draft.byId[targetId].timeStep = value.timeSliderStep;
              // When timestep is synced between slider, isTimestepAuto should be set to false.
              draft.byId[targetId].isTimestepAuto = false;
            }

            // SetAnimationStartTime
            if (value?.timeSliderAnimationStartTime!) {
              draft.byId[targetId].animationStartTime =
                value.timeSliderAnimationStartTime;
            }

            // SetAnimationEndTime
            if (value?.timeSliderAnimationEndTime!) {
              draft.byId[targetId].animationEndTime =
                value.timeSliderAnimationEndTime;
            }

            // SetAnimationDelay
            if (value?.timeSliderAnimationDelay!) {
              draft.byId[targetId].animationDelay =
                value.timeSliderAnimationDelay;
            }

            // SetTime / update actual current value
            if (value?.currentTime!) {
              const dimension: Dimension = {
                name: 'time',
                currentValue: value.currentTime,
              };

              produceDraftStateSetWebMapDimension(
                draft,
                targetId,
                dimension,
                true,
              );
              produceDraftStateSetMapDimensionFromLayerChangeDimension(
                draft,
                targetId,
                dimension,
              );
            }
          }
        });
      })
      .addCase(setBboxSync, (draft, action) => {
        const { targets: targetsFromAction, source } = action.payload;
        /* Because we want backwards compatibility with the previous code, we also need to listen to the original source action */
        const targets = [];
        if (source) {
          targets.push({
            targetId: source.payload.sourceId,
            bbox: source.payload.bbox,
            srs: source.payload.srs,
          });
        }
        /* And then append the targets form the action */
        targets.push(...targetsFromAction);
        targets.forEach((payload) => {
          const { targetId, bbox, srs } = payload;
          if (draft.byId[targetId]) {
            if (bbox) {
              draft.byId[targetId].bbox = bbox;
            }
            if (srs) {
              draft.byId[targetId].srs = srs;
            }
          }
        });
      })
      .addCase(setLayerActionSync, (draft, action) => {
        /*
         * This GENERIC_SYNC_SETLAYERACTIONS action is generated by the syncgroup listener.
         * It has multiple targets (Layers) in its payload.
         * These targets can be used as payloads in new Layer actions.
         * These actions are here handled via the layer reducer, as it is the same logic
         */
        const { targets, source } = action.payload;

        return targets.reduce(
          (prevState: WebMapState, target: SyncLayerPayloads): WebMapState => {
            const actionType = source?.type || 'GENERIC_SYNC_SETLAYERACTIONS';
            const action = {
              payload: target,
              type: actionType,
            };
            /* Handle the Layer action with the same logic, using the same reducer */
            return mapReducer(prevState, action);
          },
          draft,
        );
      })
      .addCase(
        layerActions.onUpdateLayerInformation,
        (draft, action: PayloadAction<UpdateLayerInfoPayload>) => {
          const { mapDimensions } = action.payload;
          if (!mapDimensions) {
            return draft;
          }

          const { dimensions, mapId } = mapDimensions;
          dimensions.forEach((dimension) =>
            produceDraftStateSetWebMapDimension(draft, mapId, dimension, false),
          );
          return draft;
        },
      )
      .addCase(mapChangeDimension, (draft, action) => {
        const { mapId, dimension: dimensionFromAction } = action.payload;
        produceDraftStateSetWebMapDimension(
          draft,
          mapId,
          dimensionFromAction,
          true,
        );
      })
      .addCase(setMapPreset, (draft, action) => {
        const { mapId } = action.payload;
        if (!draft.byId[mapId]) {
          return;
        }
        // reset preset state (currently setMapPreset listener is firing actions and these are setting values)
        draft.byId[mapId].baseLayers = [];
        draft.byId[mapId].mapLayers = [];
        draft.byId[mapId].overLayers = [];
        draft.byId[mapId].isAutoUpdating = false;
        draft.byId[mapId].isAnimating = false;
        draft.byId[mapId].isTimeSliderVisible = true;
        draft.byId[mapId].isTimestepAuto = true;
        draft.byId[mapId].shouldShowZoomControls = true;
        draft.byId[mapId].timeStep = undefined;
        draft.byId[mapId].animationDelay = defaultAnimationDelayAtStart;
        draft.byId[mapId].dockedLayerManagerSize = '';
      });
  },
});

export const mapActions = {
  ...slice.actions,
  setMapPreset,
  mapChangeDimension,
};

export const { reducer: mapReducer } = slice;
